import React, { useEffect, useMemo } from "react";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { MedicalQuestion } from "../../../data-model/medicalQuestion";
import { QuestionnaireStatus } from "../../../enums/questionnaireStatus";
import { SubmissionStatus } from "../../../enums/submissionStatus";
import { useQuestionnaireAnswers } from "../hooks/useQuestionnaireAnswers";
import {
  buildEvidenceCards,
  getVisibleChildren,
} from "../utils/questionnaireUtils";
import ErrorComponent from "./errorComponent";
import InProgressComponent from "./inProgressComponent";
import MultiCorrectQuestion from "./multiCorrectQuestion";
import SingleCorrectQuestion from "./singleCorrectQuestion";
import SubjectiveQuestion from "./subjectiveQuestion";

interface PortalQuestionnaireProps {
  id: string;
  onQuestionnaireUpdated: (
    answers: MedicalQuestion[],
    isDirty: boolean,
  ) => void;
  data: {
    question_tree: MedicalQuestion[];
  };
  loading: boolean;
  error: boolean;
  hasQuestions: boolean;
}

const PortalQuestionnaire = (props: PortalQuestionnaireProps) => {
  const { onQuestionnaireUpdated, data, loading, error, hasQuestions } = props;

  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const order = generalOrderDetails?.order ?? null;
  const {
    answers,
    handleRadioChange,
    handleCheckChange,
    handleTextChange,
    answeredQuestions,
    isDirty,
  } = useQuestionnaireAnswers(data.question_tree);

  useEffect(() => {
    onQuestionnaireUpdated(answeredQuestions, isDirty);
  }, [answeredQuestions, isDirty, onQuestionnaireUpdated]);

  const isQuestionnaireInProgress = useMemo(() => {
    const hasLocalQuestions = data.question_tree.length > 0;
    const isSubmissionInProgress =
      order?.status?.submission_status === SubmissionStatus.IN_PROGRESS;
    return !hasLocalQuestions && isSubmissionInProgress;
  }, [data.question_tree, order?.status?.submission_status]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoaderMessage message="Fetching questionnaire details..." />
      </div>
    );
  }

  if (
    order?.status?.questionnaire_status === QuestionnaireStatus.IN_PROGRESS ||
    isQuestionnaireInProgress
  ) {
    return <InProgressComponent />;
  }

  if (
    error ||
    order?.status?.questionnaire_status === QuestionnaireStatus.ERROR ||
    !hasQuestions
  ) {
    return <ErrorComponent />;
  }

  /**
   * Recursively renders a list of questions with hierarchical step
   * numbering (e.g. 1, 1.1, 1.2, 1.2.1).
   *
   * @param prefix - Parent step label; empty string for root level.
   */
  const renderQuestions = (
    questionList: MedicalQuestion[],
    prefix: string,
  ): React.ReactNode => {
    return questionList.map((question, index) => {
      const stepNumber = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      const evidenceCards = buildEvidenceCards(question.facts);
      const childQuestions = getVisibleChildren(question, answers);
      const childContent =
        childQuestions.length > 0
          ? renderQuestions(childQuestions, stepNumber)
          : null;

      switch (question.question_type) {
        case "single_correct":
          return (
            <SingleCorrectQuestion
              key={question.id}
              question={question}
              stepNumber={stepNumber}
              selectedValue={answers[question.id] as string}
              onRadioChange={(value) => handleRadioChange(question, value)}
              evidenceCards={evidenceCards}
            >
              {childContent}
            </SingleCorrectQuestion>
          );

        case "multi_correct":
          return (
            <MultiCorrectQuestion
              key={question.id}
              question={question}
              stepNumber={stepNumber}
              checkedValues={(answers[question.id] as string[]) ?? []}
              onCheckChange={(value, checked) =>
                handleCheckChange(question, value, checked)
              }
              evidenceCards={evidenceCards}
            >
              {childContent}
            </MultiCorrectQuestion>
          );

        case "subjective":
          return (
            <SubjectiveQuestion
              key={question.id}
              question={question}
              stepNumber={stepNumber}
              textValue={(answers[question.id] as string) ?? ""}
              onTextChange={(value) => handleTextChange(question.id, value)}
              evidenceCards={evidenceCards}
            >
              {childContent}
            </SubjectiveQuestion>
          );

        default:
          return null;
      }
    });
  };

  return (
    <div className="flex flex-col gap-4 p-2">
      {renderQuestions(data.question_tree, "")}
    </div>
  );
};

export default PortalQuestionnaire;
